import { type AlertColor } from "@mui/lab";

import { ALL_CATEGORIES } from "./constants";
import {
  type Breakdown,
  type Category,
  type Insight,
  InsightsSortOrder,
  type Risks,
  type RisksBreakdownValue,
  type RiskTypeKey,
  type SavingsPeriod,
  type StatusDetails,
} from "./types";

export const getInsightId = (insight: Insight): string => `${insight.providerId}#${insight.key}`;

export const totalFromBreakdown = (isOptimized: boolean, breakdown: Breakdown): number | null => {
  let savings = 0;

  if (!isOptimized && breakdown) {
    for (const savingsData of breakdown.data) {
      savings += savingsData.value;
    }
  }

  return savings;
};

export const totalCustomProblems = (insight: Insight): number | null => {
  if (!insight.results?.customProblems?.breakdown) {
    return null;
  }

  return totalFromBreakdown(insight.results.customProblems.isOptimized, insight.results.customProblems.breakdown);
};

export const totalRisks = (
  insight: Insight,
  riskType:
    | "operationalExcellenceRisks"
    | "performanceRisks"
    | "reliabilityRisks"
    | "securityRisks"
    | "sustainabilityRisks"
): number | null => {
  const risks = insight.results?.[riskType];
  if (!risks?.breakdown) {
    return null;
  }

  if (risks.isOptimized) {
    return 0;
  }

  let total = 0;
  risks.breakdown.data.forEach((e) => {
    const value = e.value.critical + e.value.high + e.value.medium + e.value.low;
    total += value;
  });
  return total;
};

const calculateBreakdown = (
  breakdownData:
    | {
        value: RisksBreakdownValue;
      }[]
    | undefined
): RisksBreakdownValue | null => {
  if (!breakdownData) {
    return null;
  }

  const breakdown: RisksBreakdownValue = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
  };

  breakdownData.forEach((el) => {
    breakdown.critical += el.value.critical;
    breakdown.high += el.value.high;
    breakdown.medium += el.value.medium;
    breakdown.low += el.value.low;
  });

  return breakdown;
};

export const totalRisksBreakdown = (insight: Insight | null, riskType: RiskTypeKey): RisksBreakdownValue | null => {
  if (!insight) {
    return null;
  }

  return calculateBreakdown(insight.results?.[riskType]?.breakdown?.data);
};

export const totalPotentialDailySavings = (insight: Insight): number | null => {
  if (!insight.results?.potentialDailySavings?.breakdown) {
    return null;
  }

  return totalFromBreakdown(
    insight.results?.potentialDailySavings?.isOptimized,
    insight.results?.potentialDailySavings?.breakdown
  );
};

const sortByStatus = (insights: Insight[]) => {
  const statusOrder = ["actionable", "permissions needed", "optimized", "dismissed"];

  const sortedData = [...insights].sort((a, b) => {
    const statusA = a.displayStatus ? statusOrder.indexOf(a.displayStatus) : -1;
    const statusB = b.displayStatus ? statusOrder.indexOf(b.displayStatus) : -1;

    if (statusA === -1) return 1;
    if (statusB === -1) return -1;

    return statusA - statusB;
  });

  return sortedData;
};

const sortByPriority = (insights: Insight[], ascending = false) => {
  const priorityOrder = ["High", "Medium", "Low"];

  const sortedData = [...insights].sort((a, b) => {
    const priorityA = a.priority ? priorityOrder.indexOf(a.priority) : -1;
    const priorityB = b.priority ? priorityOrder.indexOf(b.priority) : -1;

    if (priorityA === -1) return 1;
    if (priorityB === -1) return -1;

    return ascending ? priorityA - priorityB : priorityB - priorityA;
  });

  return sortedData;
};

const sortByTotalPotentialDailySavings = (insights: Insight[], ascending = false) => {
  const sortedData = [...insights];

  // The "standard" order is descending - if we want to sort ascending instead, flip everything
  const multiplier = ascending ? -1 : 1;

  sortedData.sort((a, b) => {
    const savingsA = (totalPotentialDailySavings(a) || 0) + (totalCustomProblems(a) || 0);
    const savingsB = (totalPotentialDailySavings(b) || 0) + (totalCustomProblems(b) || 0);
    if (savingsA === 0) {
      // If neither having potential savings, keep order
      if (savingsB === 0) {
        return 0;
      }

      // If only B has savings, B should go first
      return 1 * multiplier;
    }

    // If only A has savings, A should go first
    if (savingsB === 0) {
      return -1 * multiplier;
    }

    // If they both have savings, whoever has more should go first
    return (savingsB - savingsA) * multiplier;
  });

  return sortedData;
};

export const sortInsights = (insights: Insight[], sortOrder: InsightsSortOrder | ""): Insight[] => {
  const sortedInsights = sortByStatus(insights);
  switch (sortOrder) {
    case InsightsSortOrder.SAVINGS_ASCENDING:
      return sortByTotalPotentialDailySavings(sortedInsights, true);
    case InsightsSortOrder.SAVINGS_DESCENDING:
      return sortByTotalPotentialDailySavings(sortedInsights, false);
    case InsightsSortOrder.PRIORITY_ASCENDING:
      return sortByPriority(sortedInsights, false);
    case InsightsSortOrder.PRIORITY_DESCENDING:
      return sortByPriority(sortedInsights, true);
  }
  return sortedInsights;
};

export const calculateTotalSavingsForTimePeriod = (insights: Insight[] | undefined, period: SavingsPeriod): number => {
  const multiplier = period === "daily" ? 1 : period === "monthly" ? 30 : 365;
  return (
    insights?.reduce((total, insight) => {
      const dailySavings =
        insight.results?.potentialDailySavings?.breakdown?.data.reduce((sum, dataItem) => sum + dataItem.value, 0) || 0;
      return total + dailySavings * multiplier;
    }, 0) ?? 0
  );
};

export const getAlert = (
  insight: Insight,
  statusDetails: StatusDetails | null = null
): { severity: AlertColor; label: string } | null => {
  // alerts for custom insights
  if (insight.customInsightAttributes && insight.displayStatus === "dismissed") {
    return {
      severity: "info",
      label: statusDetails
        ? `This insight was dismissed by ${statusDetails.userDisplayName} on ${statusDetails.date}.`
        : "This insight was dismissed.",
    };
  }

  if (insight.customInsightAttributes && insight.displayStatus === "optimized") {
    return {
      severity: "success",
      label: statusDetails
        ? `This insight was marked as optimized by ${statusDetails.userDisplayName} on ${statusDetails.date}.`
        : "This insight was marked as optimized.",
    };
  }

  // alerts for automated insights
  if (!insight.customInsightAttributes && insight.displayStatus === "dismissed") {
    return {
      severity: "info",
      label: "This insight was automatically dismissed by DoiT Cloud Intelligence™.",
    };
  }

  if (!insight.customInsightAttributes && insight.displayStatus === "optimized") {
    return {
      severity: "success",
      label: "This insight is currently optimized and no further action is required.",
    };
  }

  return null;
};

const riskSeverityMapping = {
  "aws-trusted-advisor": {
    critical: "Critical",
    high: "High (red)",
    medium: "Medium (yellow)",
    low: "Low",
  },
  gcp: {
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
  },
  custom: {
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
  },
  default: {
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
  },
};

export const convertInsightWithRisks = (
  baseRisks: Risks | null,
  mapping: { [key: string]: string }
): { breakdown: Breakdown } | null => {
  if (!baseRisks?.breakdown) {
    return null;
  }

  const data = baseRisks.breakdown.data.map((data) => {
    const severity1orMore = Object.entries(data.value).filter(([_, value]) => value >= 1);
    return severity1orMore.map(([key, value]) => ({
      baseValue: 0,
      dimensionValues: [...data.dimensionValues, mapping[key]],
      value,
    }));
  });

  return {
    ...baseRisks,
    breakdown: {
      dimensions: [...(baseRisks.breakdown?.dimensions || []), "Severity"],
      data: data.flat(),
    },
  };
};

export const convertToDisplayRiskBreakdown = (providerId: string, result: Risks | null): Breakdown | null => {
  if (result === null) {
    return null;
  }

  const mapping =
    (riskSeverityMapping[providerId] as {
      [key: string]: string;
    }) ?? riskSeverityMapping.default;

  const converted = convertInsightWithRisks(result, mapping);

  return converted ? converted.breakdown : null;
};

export const normalizeInsightCategories = (results: Insight[] | undefined): Insight[] =>
  results?.map((datum) => {
    const categories = datum.categories;
    const validCategories = categories?.filter((category): category is Category =>
      ALL_CATEGORIES.some((ac) => ac.toLowerCase() === category.toLowerCase())
    );

    return {
      ...datum,
      categories: validCategories,
    };
  }) || [];

export const calculateSavings = (savings: number) => savings * 365.25;

type CalculatePermissionMetricsRes = {
  permissionsCoverage: number;
  grantedPermissionsInsights: number;
};

export const calculatePermissionsMetrics = (
  totalInsightsNum: number,
  permissionsNeededInsightsNum: number
): CalculatePermissionMetricsRes => {
  if (totalInsightsNum === 0) {
    return {
      permissionsCoverage: 0,
      grantedPermissionsInsights: 0,
    };
  }
  const grantedPermissionsInsights = totalInsightsNum - permissionsNeededInsightsNum;
  const permissionsCoverage = Math.round((grantedPermissionsInsights / totalInsightsNum) * 100);

  return {
    permissionsCoverage,
    grantedPermissionsInsights,
  };
};
